import React, { useRef } from "react";
import LogoRelatorio from "../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../Utils/useNomeArquivoPdf";
import "./RelatorioProgramaTreinamentosPrint.css";

const ID_QUADRO_IMPRESSAO = "quadro-impressao-programa-treinamentos";
const LARGURAS_COLUNAS = ["18%", "27%", "17%", "11%", "10%", "10%", "7%"];

const ESTILOS_IMPRESSAO_PROGRAMA_TREINAMENTOS = `
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #071b31; font-family: Arial, sans-serif; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  .pagina-pdf-programa-treinamentos { width: 100%; }
  .cabecalho-pdf-programa-treinamentos, .tabela-pdf-programa-treinamentos { width: 100%; border-collapse: collapse; table-layout: fixed; }
  .cabecalho-pdf-programa-treinamentos td { border: 1px solid #153d64; padding: 6px; text-align: center; }
  .cabecalho-pdf-programa-treinamentos td:first-child { width: 28mm; }
  .cabecalho-pdf-programa-treinamentos td:last-child { width: 40mm; }
  .cabecalho-pdf-programa-treinamentos img { max-width: 26mm; max-height: 15mm; }
  .cabecalho-pdf-programa-treinamentos strong, .cabecalho-pdf-programa-treinamentos span { display: block; }
  .cabecalho-pdf-programa-treinamentos td:nth-child(2) strong { color: #0b4b78; font-size: 16px; }
  .cabecalho-pdf-programa-treinamentos span { margin-top: 3px; font-size: 12px; }
  .tabela-pdf-programa-treinamentos { margin-top: 4mm; font-size: 12px; }
  .tabela-pdf-programa-treinamentos th, .tabela-pdf-programa-treinamentos td { border: 1px solid #6aa0d8; padding: 5px 4px; text-align: center; vertical-align: middle; overflow-wrap: anywhere; white-space: pre-line; }
  .tabela-pdf-programa-treinamentos th { background: #0b4774; color: #fff; font-size: 12px; }
  .tabela-pdf-programa-treinamentos td:first-child { color: #267326; font-weight: 700; }
  .tabela-pdf-programa-treinamentos thead { display: table-header-group; }
  .tabela-pdf-programa-treinamentos tr { break-inside: avoid; page-break-inside: avoid; }
  .tabela-pdf-programa-treinamentos tbody tr:nth-child(even) { background: #f4f7f9; }
  .status-pdf-programa-treinamentos { display: inline-block; padding: 3px 6px; border-radius: 10px; color: #fff; font-size: 12px; font-weight: 700; }
`;

const valor = (conteudo) => String(conteudo ?? "").trim() || "-";

function formatarData(data) {
  if (!data) return "-";
  const [ano, mes, dia] = String(data).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : data;
}

function corStatus(status) {
  if (status === "Realizado") return "#078568";
  if (status === "Em andamento") return "#ed6c02";
  if (status === "Cancelado") return "#d32f2f";
  return "#1976d2";
}

function imprimirProgramaTreinamentos(elemento, nomeArquivo) {
  if (!elemento) return;

  document.getElementById(ID_QUADRO_IMPRESSAO)?.remove();
  const quadroImpressao = document.createElement("iframe");
  quadroImpressao.id = ID_QUADRO_IMPRESSAO;
  quadroImpressao.title = "Impressão do Programa de Treinamentos";
  Object.assign(quadroImpressao.style, { position: "fixed", right: "0", bottom: "0", width: "1px", height: "1px", border: "0", opacity: "0", pointerEvents: "none" });
  document.body.appendChild(quadroImpressao);

  const janelaImpressao = quadroImpressao.contentWindow;
  const documentoImpressao = quadroImpressao.contentDocument;
  documentoImpressao.open();
  documentoImpressao.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${nomeArquivo}</title><style>${ESTILOS_IMPRESSAO_PROGRAMA_TREINAMENTOS}</style></head><body></body></html>`);
  documentoImpressao.close();
  documentoImpressao.body.appendChild(elemento.cloneNode(true));

  const imagens = Array.from(documentoImpressao.images);
  Promise.all(imagens.map((imagem) => imagem.complete ? Promise.resolve() : new Promise((resolve) => {
    imagem.addEventListener("load", resolve, { once: true });
    imagem.addEventListener("error", resolve, { once: true });
  }))).then(() => {
    janelaImpressao.onafterprint = () => quadroImpressao.remove();
    janelaImpressao.focus();
    janelaImpressao.print();
  });
}

export default function RelatorioProgramaTreinamentosPrint({ programa, onVoltar }) {
  const paginaRef = useRef(null);
  const nomeArquivo = `RQ-5.9-PROGRAMA-DE-TREINAMENTOS-REV-${valor(programa?.versao)}`;
  const treinamentos = Array.isArray(programa?.treinamentos) ? programa.treinamentos : [];
  useNomeArquivoPdf(nomeArquivo);

  if (!programa) return null;

  return (
    <div className="relatorio-programa-treinamentos-print">
      <div className="acoes-relatorio-programa-treinamentos">
        <button type="button" onClick={onVoltar}>Voltar</button>
        <button type="button" className="botao-pdf-programa-treinamentos" onClick={() => imprimirProgramaTreinamentos(paginaRef.current, nomeArquivo)}>Salvar como PDF</button>
      </div>
      <main ref={paginaRef} className="pagina-pdf-programa-treinamentos">
        <table className="cabecalho-pdf-programa-treinamentos">
          <tbody>
            <tr>
              <td><LogoRelatorio alt="Logo da empresa" /></td>
              <td><strong>PROGRAMA DE TREINAMENTOS</strong><span>Sistema de Gestão da Qualidade</span></td>
              <td><strong>RQ 5.9</strong><span>Revisão {valor(programa.versao)}</span><span>Aprovado em {formatarData(programa.aprovadoEm)}</span></td>
            </tr>
          </tbody>
        </table>

        <table className="tabela-pdf-programa-treinamentos">
          <colgroup>{LARGURAS_COLUNAS.map((largura, indice) => <col key={indice} style={{ width: largura }} />)}</colgroup>
          <thead>
            <tr>
              <th>Treinamento planejado</th>
              <th>Conteúdo programático</th>
              <th>Público-alvo</th>
              <th>Frequência/Prazo</th>
              <th>Instrutor</th>
              <th>Mês previsto</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {treinamentos.map((item, indice) => (
              <tr key={`${item.treinamentoPlanejado || "treinamento"}-${indice}`}>
                <td>{valor(item.treinamentoPlanejado)}</td>
                <td>{valor(item.conteudoProgramatico)}</td>
                <td>{valor(item.publicoAlvo)}</td>
                <td>{valor(item.frequenciaPrazo)}</td>
                <td>{valor(item.instrutor)}</td>
                <td>{valor(item.mesPrevisto)}</td>
                <td><span className="status-pdf-programa-treinamentos" style={{ backgroundColor: corStatus(item.status) }}>{valor(item.status || "Planejado")}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </main>
    </div>
  );
}
